import React, { useState, useEffect, useRef } from "react";
import { Menu, X, Landmark, Sliders, Shield, Lock, LogOut, User, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

export default function Header() {
  const { user, setAuthModalOpen, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // ⚡ Bolt Optimization: Use requestAnimationFrame to throttle scroll events,
          // preventing main thread blocking and unnecessary re-renders during rapid scrolling.
          // Expected impact: Smoother scroll performance, reduced CPU usage.
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    { label: "Fractional CAIO", href: "/" },
    { label: "Strategic Advisory", href: "/strategic-advisory" },
    { label: "Project Portfolio", href: "/portfolio" },
    { label: "Insights", href: "/insights" }
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled 
        ? "bg-brand-charcoal/95 border-b-4 border-brand-burgundy shadow-xl py-2" 
        : "bg-brand-charcoal border-b-4 border-brand-burgundy py-4"
    }`}>
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Brand Logo - Robust & Industrial Bold style resembling a franchise mark */}
        <Link 
          to="/" 
          onClick={() => { 
            if (currentPath === "/") {
              window.scrollTo({ top: 0, behavior: "smooth" }); 
            }
          }}
          className="flex items-center gap-3 group focus:outline-none"
        >
          <span className="flex items-center justify-center w-10 h-10 rounded-sm bg-brand-burgundy border-2 border-brand-orange text-white font-display font-bold text-xl shadow-md group-hover:scale-105 transition-transform">
            C
          </span>
          <div className="text-left leading-none">
            <div className="text-white font-display font-bold text-lg sm:text-xl tracking-tight uppercase">
              CRISPIN <span className="text-brand-orange font-light">COURTENAY</span>
            </div>
            <div className="text-[10px] md:text-xs font-mono text-brand-sand/60 tracking-widest mt-1 uppercase">
              Strategic AI Systems
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex space-x-6 text-xs font-mono uppercase tracking-wider text-brand-sand/80">
          {menuItems.map((item) => {
            const isActive = currentPath === item.href || (item.href === "/" && (currentPath === "/fractional-caio" || currentPath === "/caio-diagnostic"));
            return (
              <Link 
                key={item.label}
                to={item.href} 
                onClick={handleNavClick}
                className={`transition-all py-1 font-semibold hover:text-brand-orange border-b-2 ${
                  isActive 
                    ? "text-brand-orange border-brand-orange font-bold" 
                    : "text-brand-sand border-transparent hover:border-brand-orange/40"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Action Buttons & Auth */}
        <div className="hidden md:flex items-center gap-4">

          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="relative flex items-center justify-center w-10 h-10 bg-brand-sand hover:bg-brand-sand/80 text-brand-burgundy rounded-sm border border-brand-burgundy/20 hover:border-brand-burgundy transition-all shadow-md focus:outline-none cursor-pointer"
                aria-label="User profile menu"
              >
                {user.role === "admin" ? (
                  <Shield className="w-4 h-4 text-brand-burgundy" />
                ) : (
                  <User className="w-4 h-4 text-brand-burgundy" />
                )}
                {/* Active Session Status Dot */}
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-brand-green border-2 border-brand-charcoal rounded-full animate-pulse" />
              </button>

              {/* Dropdown Card */}
              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2.5 w-56 bg-brand-cream border-2 border-brand-burgundy rounded-sm shadow-xl overflow-hidden text-brand-charcoal z-50 font-mono text-[10px]"
                  >
                    {/* Role Header */}
                    <div className="bg-brand-burgundy text-white px-4 py-2.5 border-b border-brand-orange/20 select-none">
                      <span className="uppercase font-bold tracking-wider text-[9px] text-brand-orange">
                        {user.role === "admin" ? "Administrative Access" : "Client Portal"}
                      </span>
                    </div>

                    {/* Metadata Section */}
                    <div className="p-4 space-y-2 border-b border-brand-sand">
                      <div>
                        <span className="text-stone-400 uppercase tracking-widest block text-[8px]">Session Identity</span>
                        <span className="font-bold text-brand-charcoal text-[11px] truncate block mt-0.5" title={user.username}>
                          {user.username}
                        </span>
                      </div>
                      <div>
                        <span className="text-stone-400 uppercase tracking-widest block text-[8px]">Auth Provider</span>
                        <span className="font-bold text-stone-600 block uppercase mt-0.5">
                          ✦ {user.provider}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="bg-brand-sand/40 p-2 space-y-1">
                      {user.role === 'admin' && (
                        <button
                          onClick={() => {
                            window.location.assign("/admin");
                          }}
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-brand-charcoal text-brand-orange hover:text-brand-orange/80 border border-brand-orange/50 transition-colors rounded-sm font-bold tracking-wider uppercase cursor-pointer mb-2"
                        >
                          <LayoutDashboard className="w-3.5 h-3.5" /> Enter Admin Hub
                        </button>
                      )}
                      <button
                        onClick={() => {
                          logout();
                          setDropdownOpen(false);
                        }}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-brand-burgundy text-white hover:bg-brand-burgundy/90 transition-colors rounded-sm font-bold tracking-wider uppercase cursor-pointer"
                      >
                        <LogOut className="w-3.5 h-3.5" /> Sign Out Session
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button
              onClick={() => setAuthModalOpen(true)}
              title="Secure Client/Admin Login"
              aria-label="Secure Client/Admin Login"
              className="p-3 bg-brand-sand border border-brand-sand hover:border-brand-burgundy/50 text-brand-burgundy hover:text-brand-orange rounded-sm transition-all shadow-md focus:outline-none cursor-pointer"
            >
              <Lock className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Mobile Burger Toggle */}
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-brand-sand hover:text-brand-orange p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
          aria-label="Toggle navigation menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-brand-burgundy/40 bg-brand-charcoal overflow-hidden"
          >
            <div className="px-6 py-8 space-y-5 flex flex-col">
              {menuItems.map((item) => {
                const isActive = currentPath === item.href || (item.href === "/" && currentPath === "/fractional-caio");
                return (
                  <Link 
                    key={item.label}
                    to={item.href} 
                    onClick={handleNavClick}
                    className={`text-base font-semibold transition-colors ${
                      isActive ? "text-brand-orange font-bold" : "text-brand-sand hover:text-brand-orange"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {user ? (
                <div className="flex items-center justify-between bg-brand-sand/20 border border-brand-sand/40 p-3 rounded-sm mt-2">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-mono font-bold text-brand-orange uppercase leading-none">
                      {user.role === "admin" ? "ADMIN ACTIVE" : "CLIENT ACTIVE"}
                    </span>
                    <span className="text-[9px] font-mono text-brand-sand/70 mt-1 truncate max-w-[120px]">
                      {user.username}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-brand-burgundy/80 text-white font-mono text-xs rounded-sm hover:bg-brand-burgundy transition-colors cursor-pointer"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setAuthModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand-sand text-brand-burgundy font-mono font-bold text-xs rounded-sm hover:bg-brand-sand/80 transition-colors mt-2 cursor-pointer"
                >
                  <Lock className="w-4 h-4" /> Secure Portal Login
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
